<template>
  <AutoComplete
    v-if="options"
    :modelValue="transformedModelValue"
    @update:modelValue="updateModelValue($event)"
    dropdown
    :suggestions="suggestions"
    @complete="search"
    optionLabel="label"
    optionValue="value"
    :multiple="multiple"
    :typeahead="typeahead"
    :disabled="isLoading"
    :class="fullWidth ? 'w-full [&_input]:w-full!' : '[&_input]:field-sizing-content'"></AutoComplete>
</template>

<script setup lang="ts">
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { storeToRefs } from 'pinia'
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete'
import { computed, onMounted, ref, type PropType } from 'vue'

interface Option {
  value: string | number
  label: string
}

const props = defineProps({
  modelValue: {
    type: [String, Array] as PropType<string | (string | number)[]>,
    required: false
  },
  options: {
    type: Function as PropType<() => Option[] | Promise<Option[]>>,
    required: true
  },
  multiple: {
    type: Boolean,
    default: false
  },
  arbitrary: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: false
  },
  typeahead: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const suggestions = ref<Option[]>([])
const localOptions = ref<Option[]>([])
const isLoading = ref(true)

const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)

onMounted(async () => {
  try {
    isLoading.value = true
    const responseValue = await props.options()
    localOptions.value = responseValue
  } catch (error) {
    console.error('Error loading options:', error)
    localOptions.value = []
  } finally {
    isLoading.value = false
  }
})

const search = async (event: AutoCompleteCompleteEvent) => {
  const query = event.query
  suggestions.value = localOptions.value.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
}

const transformModelValue = (id: string | number | null | undefined) => {
  // No value selected — return null so AutoComplete shows the placeholder
  if (id === null || id === undefined || id === '') {
    return null
  }
  const option = localOptions.value.find((item) =>
    typeof item.value === typeof id ? item.value === id : Number(item.value) === Number(id)
  )
  // Don't show "Unknown" while loading - just show the ID or empty
  return { value: id, label: option?.label || (isLoading.value ? '' : 'Unknown') }
}

const transformedModelValue = computed(() => {
  if (props.arbitrary) {
    return props.modelValue
  }
  if (props.multiple) {
    return ((props.modelValue || []) as (string | number)[]).map(transformModelValue)
  }
  return transformModelValue(props.modelValue as string | undefined)
})

const updateModelValue = (arr: Option | string | (Option | string)[] | null) => {
  if (Array.isArray(arr)) {
    emit(
      'update:modelValue',
      arr.map((item) => (item as Option).value || item)
    )
  } else {
    emit('update:modelValue', (arr as Option).value || arr)
  }
}
</script>
